import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
  BeakerIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AcademicCapIcon,
  GlobeAltIcon,
  TrophyIcon,
  SparklesIcon,
  RocketLaunchIcon,
  HeartIcon,
  ChatBubbleOvalLeftIcon,
  UserIcon,
  ClockIcon,
  ChevronDownIcon,
  PlusIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolidIcon } from "@heroicons/react/24/solid";

const Homepage = () => {
  // Authentication check - redirect to login if not authenticated
  useEffect(() => {
    const isAuthenticated = localStorage.getItem("isAuthenticated");
    if (!isAuthenticated) {
      // Use window.location for redirect to avoid navigation hook issues
      window.location.href = "/login";
      return;
    }
  }, []);

  // Other existing states
  const [pollAnswer, setPollAnswer] = useState("");
  const [currentPollIndex, setCurrentPollIndex] = useState(0);
  const [likedPosts, setLikedPosts] = useState(new Set());
  // eslint-disable-next-line no-unused-vars
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showNotifications, setShowNotifications] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showQuickLinksDropdown, setShowQuickLinksDropdown] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showShortcutsDropdown] = useState(false);

  // Navigation hook
  const navigate = useNavigate();

  const [shortcuts, setShortcuts] = useState([
    {
      id: 1,
      name: "HIS Portal",
      url: "https://his.apollo.com",
      icon: "🏥",
      category: "Healthcare",
    },
    {
      id: 2,
      name: "Email Outlook",
      url: "https://outlook.office.com",
      icon: "📧",
      category: "Communication",
    },
    {
      id: 3,
      name: "Learning Center",
      url: "https://learning.apollo.com",
      icon: "🎓",
      category: "Learning",
    },
  ]);
  const [showAddShortcut, setShowAddShortcut] = useState(false);
  const [newShortcut, setNewShortcut] = useState({
    name: "",
    url: "",
    icon: "🔗",
    category: "General",
  });
  const [chatMessages, setChatMessages] = useState([
    {
      id: 1,
      type: "ai",
      message: "Hello! I'm Apollo AI Assistant. How can I help you today?",
      timestamp: new Date(),
    },
  ]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: "System",
      icon: "✅",
      message: "Your password was updated successfully.",
      time: "2 mins ago",
      read: false,
      color: "bg-green-50 border-green-200 text-green-800",
    },
    {
      id: 2,
      type: "HR",
      icon: "📢",
      message:
        'New HR policy on "Remote Work" is now live. Review before July 30.',
      time: "1 hour ago",
      read: false,
      color: "bg-blue-50 border-blue-200 text-blue-800",
    },
    {
      id: 3,
      type: "IT",
      icon: "🛠️",
      message:
        "Scheduled maintenance on the Helpdesk Portal: July 25, 10 PM–12 AM.",
      time: "3 hours ago",
      read: false,
      color: "bg-orange-50 border-orange-200 text-orange-800",
    },
    {
      id: 4,
      type: "Workflow",
      icon: "🔄",
      message:
        'You\'ve been assigned as approver for "Clinical Training Form" submitted by Dr. Rao.',
      time: "5 hours ago",
      read: true,
      color: "bg-purple-50 border-purple-200 text-purple-800",
    },
    {
      id: 5,
      type: "Polls",
      icon: "📊",
      message: 'A new poll on "Intranet Feature Feedback" is live. Vote now!',
      time: "1 day ago",
      read: true,
      color: "bg-indigo-50 border-indigo-200 text-indigo-800",
    },
    {
      id: 6,
      type: "Learning",
      icon: "🎓",
      message:
        'New training module on "Data Security Fundamentals" is available in the Learning Center.',
      time: "2 days ago",
      read: true,
      color: "bg-emerald-50 border-emerald-200 text-emerald-800",
    },
  ]);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const quickLinksRef = useRef(null);
  const createDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
      if (
        quickLinksRef.current &&
        !quickLinksRef.current.contains(event.target)
      ) {
        setShowQuickLinksDropdown(false);
      }
      if (
        createDropdownRef.current &&
        !createDropdownRef.current.contains(event.target)
      ) {
        setShowCreateDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Toggle like function
  const toggleLike = (postId) => {
    setLikedPosts((prev) => {
      const newLiked = new Set(prev);
      if (newLiked.has(postId)) {
        newLiked.delete(postId);
      } else {
        newLiked.add(postId);
      }
      return newLiked;
    });
  };

  // Toggle dark mode
  // eslint-disable-next-line no-unused-vars
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Mark notification as read
  // eslint-disable-next-line no-unused-vars
  const markAsRead = (notificationId) => {
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
  };

  // Get unread notifications count
  // eslint-disable-next-line no-unused-vars
  const unreadCount = notifications.filter((notif) => !notif.read).length;

  // AI Chat functions
  const toggleAIChat = () => {
    setShowAIChat(!showAIChat);
  };

  const sendMessage = () => {
    if (currentMessage.trim()) {
      // Add user message
      const userMessage = {
        id: Date.now(),
        type: "user",
        message: currentMessage,
        timestamp: new Date(),
      };

      setChatMessages((prev) => [...prev, userMessage]);
      setCurrentMessage("");

      // Simulate AI response after a short delay
      setTimeout(() => {
        const aiResponse = {
          id: Date.now() + 1,
          type: "ai",
          message: getAIResponse(currentMessage),
          timestamp: new Date(),
        };
        setChatMessages((prev) => [...prev, aiResponse]);
      }, 1000);
    }
  };

  const getAIResponse = (userMessage) => {
    const message = userMessage.toLowerCase();
    if (message.includes("attendance") || message.includes("time")) {
      return "I can help you with attendance tracking, leave applications, and time management. Would you like me to show you your current attendance status?";
    } else if (message.includes("payroll") || message.includes("salary")) {
      return "For payroll inquiries, I can guide you to download pay slips, view salary structure, or access tax documents. What specifically do you need help with?";
    } else if (message.includes("wellness") || message.includes("health")) {
      return "Our wellness programs include health screenings, mental wellness support, and fitness sessions. Would you like to explore available wellness programs?";
    } else if (message.includes("resources") || message.includes("training")) {
      return "I can help you access training materials, policy documents, and professional development resources. What type of resource are you looking for?";
    } else {
      return "I'm here to help you navigate Apollo's healthcare services. You can ask me about attendance, payroll, wellness programs, or resources. How can I assist you today?";
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Get last login info
  // eslint-disable-next-line no-unused-vars
  const getLastLoginInfo = () => {
    const now = new Date();
    const lastLogin = new Date(now.getTime() - 2 * 60 * 60 * 1000); // 2 hours ago
    return lastLogin.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Shortcuts management functions
  const addShortcut = () => {
    if (newShortcut.name.trim() && newShortcut.url.trim()) {
      const shortcut = {
        id: Date.now(),
        ...newShortcut,
        url: newShortcut.url.startsWith("http")
          ? newShortcut.url
          : `https://${newShortcut.url}`,
      };
      setShortcuts((prev) => [...prev, shortcut]);
      setNewShortcut({ name: "", url: "", icon: "🔗", category: "General" });
      setShowAddShortcut(false);
    }
  };

  const removeShortcut = (id) => {
    setShortcuts((prev) => prev.filter((shortcut) => shortcut.id !== id));
  };

  const openShortcut = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Smooth scroll to section function
  // eslint-disable-next-line no-unused-vars
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Enhanced portal statistics with growth indicators
  const portalStats = [
    {
      value: "25K+",
      label: "Connected Users",
      icon: UserGroupIcon,
      color: "bg-gradient-to-br from-apollo-accent to-blue-600",
      growth: "+12% this quarter",
      description: "Active workforce worldwide",
    },
    {
      value: "50K+",
      label: "Daily Interactions",
      icon: ChatBubbleLeftRightIcon,
      color: "bg-gradient-to-br from-emerald-500 to-emerald-700",
      growth: "+28% engagement",
      description: "Messages, posts & collaboration",
    },
    {
      value: "95%",
      label: "Portal Adoption",
      icon: TrophyIcon,
      color: "bg-gradient-to-br from-purple-500 to-purple-700",
      growth: "Industry leading",
      description: "User satisfaction rate",
    },
    {
      value: "4.8hrs",
      label: "Average Time Spent by User",
      icon: RocketLaunchIcon,
      color: "bg-gradient-to-br from-orange-500 to-red-600",
      growth: "Optimal engagement",
      description: "Daily platform usage time",
    },
  ];

  // Multiple polls/surveys data
  const pollsData = [
    {
      id: 1,
      question: "How do you feel about our new wellness programs?",
      options: [
        { value: "excellent", label: "Excellent initiatives" },
        { value: "good", label: "Good progress" },
        { value: "needs-improvement", label: "Needs improvement" },
      ],
    },
    {
      id: 2,
      question: "How satisfied are you with the new digital tools?",
      options: [
        { value: "very-satisfied", label: "Very satisfied" },
        { value: "satisfied", label: "Satisfied" },
        { value: "neutral", label: "Neutral" },
        { value: "dissatisfied", label: "Dissatisfied" },
      ],
    },
    {
      id: 3,
      question: "What's your preferred communication method?",
      options: [
        { value: "email", label: "Email notifications" },
        { value: "teams", label: "Microsoft Teams" },
        { value: "mobile", label: "Mobile app alerts" },
        { value: "portal", label: "Portal dashboard" },
      ],
    },
  ];

  // Sample discussion posts data
  const discussionPosts = [
    {
      id: 1,
      title: "New AI-Powered Diagnostic Tools: Game Changer for Radiology",
      image:
        "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop",
      author: "Dr. Rajesh Kumar",
      department: "Radiology",
      likes: 42,
      comments: 18,
      timeAgo: "2 hours ago",
    },
    {
      id: 2,
      title: "Mental Health Support Programs: Staff Wellness Initiative",
      image:
        "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=250&fit=crop",
      author: "Kavya Patel",
      department: "HR Wellness",
      likes: 38,
      comments: 24,
      timeAgo: "5 hours ago",
    },
    {
      id: 3,
      title: "Breakthrough in Cardiac Surgery: Minimally Invasive Procedures",
      image:
        "https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=400&h=250&fit=crop&auto=format",
      author: "Dr. Sushma Iyer",
      department: "Cardiac Surgery",
      likes: 67,
      comments: 31,
      timeAgo: "1 day ago",
    },
    {
      id: 4,
      title: "Digital Health Records: Streamlining Healthcare Worldwide",
      image:
        "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=250&fit=crop",
      author: "Vikram Malhotra",
      department: "IT Innovation",
      likes: 29,
      comments: 12,
      timeAgo: "2 days ago",
    },
    {
      id: 5,
      title: "Nursing Excellence Awards: Celebrating Our Healthcare Heroes",
      image:
        "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=250&fit=crop",
      author: "Ritu Agarwal",
      department: "Nursing Administration",
      likes: 84,
      comments: 47,
      timeAgo: "3 days ago",
    },
    {
      id: 6,
      title: "Telemedicine Expansion: Reaching Remote Communities",
      image:
        "https://images.unsplash.com/photo-1559757175-8700a29ce4ba?w=400&h=250&fit=crop",
      author: "Dr. Rohit Kapoor",
      department: "Telemedicine",
      likes: 56,
      comments: 33,
      timeAgo: "4 days ago",
    },
  ];

  // Task management data
  const myTasks = [
    {
      id: 1,
      title: "Complete Annual Health Assessment",
      assignedBy: "Dr. Meera Gupta",
      dueDate: "July 25, 2025",
      priority: "High",
      status: "In Progress",
      description: "Annual mandatory health screening and assessment",
    },
    {
      id: 2,
      title: "Review New Medical Protocol Guidelines",
      assignedBy: "Medical Director",
      dueDate: "July 30, 2025",
      priority: "Medium",
      status: "Pending",
      description: "Review and acknowledge updated treatment protocols",
    },
    {
      id: 3,
      title: "Submit Monthly Quality Metrics",
      assignedBy: "Quality Assurance Team",
      dueDate: "August 1, 2025",
      priority: "High",
      status: "Not Started",
      description: "Compile and submit departmental quality metrics",
    },
  ];

  // Recent documents data
  const recentDocs = [
    {
      id: 1,
      name: "Healthcare Policy Updates Q3 2025",
      type: "PDF",
      size: "2.4 MB",
      lastModified: "2 hours ago",
      author: "Policy Team",
    },
    {
      id: 2,
      name: "Equipment Maintenance Schedule",
      type: "Excel",
      size: "156 KB",
      lastModified: "1 day ago",
      author: "Facilities Team",
    },
    {
      id: 3,
      name: "Training Module - Emergency Procedures",
      type: "Video",
      size: "45.2 MB",
      lastModified: "3 days ago",
      author: "Training Department",
    },
  ];

  // Forms data
  const myForms = [
    {
      id: 1,
      name: "Time Off Request",
      category: "HR",
      status: "Draft",
      lastUpdated: "Today",
    },
    {
      id: 2,
      name: "Equipment Request Form",
      category: "Facilities",
      status: "Submitted",
      lastUpdated: "Yesterday",
    },
    {
      id: 3,
      name: "Professional Development Plan",
      category: "Learning",
      status: "Approved",
      lastUpdated: "3 days ago",
    },
  ];

  // New colleagues data
  const newColleagues = [
    {
      id: 1,
      name: "Dr. Anita Reddy",
      position: "Cardiologist",
      department: "Cardiology",
      joinDate: "July 15, 2025",
      image:
        "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face",
    },
    {
      id: 2,
      name: "Arjun Singh",
      position: "Nurse Practitioner",
      department: "Emergency Medicine",
      joinDate: "July 18, 2025",
      image:
        "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=150&h=150&fit=crop&crop=face",
    },
    {
      id: 3,
      name: "Deepika Joshi",
      position: "Medical Technologist",
      department: "Laboratory Services",
      joinDate: "July 20, 2025",
      image:
        "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face&auto=format",
    },
  ];

  // Documents/Forms categories
  // eslint-disable-next-line no-unused-vars
  const documentCategories = [
    { name: "HR Documents", count: 12, icon: "👥" },
    { name: "Medical Forms", count: 8, icon: "🏥" },
    { name: "Training Materials", count: 15, icon: "📚" },
    { name: "Policies & Procedures", count: 22, icon: "📋" },
    { name: "Equipment Requests", count: 5, icon: "🔧" },
    { name: "Quality Reports", count: 9, icon: "📊" },
  ];

  return (
    <div className="min-h-screen bg-apollo-gray">
      {/* Header Component */}
      <Header />

      {/* Beautiful Single Hero Banner */}
      <section
        id="hero"
        className="relative bg-gradient-to-br from-apollo-primary via-apollo-secondary to-apollo-accent text-white py-6 overflow-hidden"
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 right-10 w-96 h-96 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
          <div
            className="absolute top-1/2 left-1/3 w-64 h-64 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "2s" }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center min-h-[210px]">
            {/* Left Side - Content */}
            <div className="space-y-4">
              {/* Welcome Message */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-blue-300/30">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-sm">DS</span>
                  </div>
                  <div>
                    <div className="text-white font-bold text-lg">
                      Welcome back, Dr. Priya Sharma
                    </div>
                    <div className="text-blue-200 text-xs">
                      Cardiology Department • Apollo Main Hospital
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div 
                    onClick={() => navigate("/tasks")}
                    className="text-center cursor-pointer hover:bg-white/10 rounded-lg p-2 transition-all duration-300 hover:scale-105"
                  >
                    <div className="text-xl font-bold text-white">12</div>
                    <div className="text-xs text-blue-200">Tasks Today</div>
                  </div>
                  <div 
                    onClick={() => navigate("/notifications")}
                    className="text-center cursor-pointer hover:bg-white/10 rounded-lg p-2 transition-all duration-300 hover:scale-105"
                  >
                    <div className="text-xl font-bold text-white">3</div>
                    <div className="text-xs text-blue-200">Pending Items</div>
                  </div>
                  <div 
                    onClick={() => navigate("/forums")}
                    className="text-center cursor-pointer hover:bg-white/10 rounded-lg p-2 transition-all duration-300 hover:scale-105"
                  >
                    <div className="text-xl font-bold text-white">8</div>
                    <div className="text-xs text-blue-200">Messages</div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 backdrop-blur-sm rounded-full text-xs font-medium border border-blue-300/30">
                  <UserGroupIcon className="w-3 h-3 mr-1 text-blue-300" />
                  Apollo Digital Platform
                </span>
              </div>

              <div className="relative">
                <h1 className="text-3xl md:text-4xl font-black leading-tight mb-3">
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-white to-indigo-300">
                    Your Healthcare
                  </span>
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 via-blue-300 to-emerald-300">
                    Command Center
                  </span>
                </h1>

                {/* Floating Graphics */}
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-lg rotate-12 animate-bounce opacity-80"></div>
                <div className="absolute top-12 -left-3 w-6 h-6 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full animate-pulse"></div>
              </div>

              <p className="text-base md:text-lg text-blue-100 mb-4 max-w-2xl leading-relaxed">
                Streamline your healthcare workflow with intelligent tools,
                real-time collaboration, and secure data access.
              </p>

              {/* Quick Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-sm"
                >
                  View Dashboard
                </button>
                <button 
                  onClick={() => navigate('/tasks')}
                  className="px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/30 text-white rounded-lg font-semibold hover:bg-white/20 transition-all duration-300 text-sm"
                >
                  Quick Access
                </button>
              </div>
            </div>

            {/* Right Side - Floating Statistics Cards */}
            <div className="relative w-full h-[400px] flex items-center justify-center">
              {/* Documents Card */}
              <div 
                onClick={() => navigate('/documents')}
                className="absolute top-0 left-0 bg-white rounded-xl p-4 shadow-xl transform -rotate-12 hover:rotate-0 transition-all duration-500 hover:scale-105 group cursor-pointer"
              >
                <div className="text-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <DocumentTextIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-apollo-primary group-hover:text-blue-600 transition-colors">
                    24
                  </div>
                  <div className="text-xs text-gray-600 font-medium">
                    Documents
                  </div>
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-400 rounded-full animate-pulse"></div>
              </div>

              {/* Forms Card */}
              <div 
                onClick={() => navigate('/forms')}
                className="absolute top-0 right-0 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl p-4 shadow-xl transform rotate-12 hover:rotate-0 transition-all duration-500 hover:scale-105 group cursor-pointer text-white"
              >
                <div className="text-center">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <ClipboardDocumentListIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-2xl font-bold group-hover:scale-110 transition-transform">
                    8
                  </div>
                  <div className="text-xs opacity-90 font-medium">Forms</div>
                </div>
              </div>

              {/* Messages Card */}
              <div 
                onClick={() => navigate('/notifications')}
                className="absolute top-16 right-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl p-4 shadow-xl transform rotate-15 hover:rotate-0 transition-all duration-500 hover:scale-105 group cursor-pointer text-white"
              >
                <div className="text-center">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-2xl font-bold group-hover:scale-110 transition-transform">
                    18
                  </div>
                  <div className="text-xs opacity-90 font-medium">Messages</div>
                </div>
                <div className="absolute -top-1 -left-1 w-3 h-3 bg-lime-400 rounded-full animate-pulse"></div>
              </div>

              {/* Tasks Card */}
              <div 
                onClick={() => navigate('/tasks')}
                className="absolute bottom-16 left-0 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl p-4 shadow-xl transform rotate-6 hover:rotate-0 transition-all duration-500 hover:scale-105 group cursor-pointer text-white"
              >
                <div className="text-center">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <ClipboardDocumentCheckIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-2xl font-bold group-hover:scale-110 transition-transform">
                    12
                  </div>
                  <div className="text-xs opacity-90 font-medium">Tasks</div>
                </div>
                <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-yellow-[170px]0 rounded-full animate-bounce"></div>
              </div>

              {/* Alerts Card */}
              <div 
                onClick={() => navigate('/notifications')}
                className="absolute bottom-0 right-0 bg-white rounded-xl p-4 shadow-xl transform -rotate-6 hover:rotate-0 transition-all duration-500 hover:scale-105 group cursor-pointer border-2 border-orange-200"
              >
                <div className="text-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-orange-400 to-red-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <BellIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-apollo-primary group-hover:text-orange-600 transition-colors">
                    5
                  </div>
                  <div className="text-xs text-gray-600 font-medium">
                    Alerts
                  </div>
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-ping"></div>
              </div>

              {/* Team Card (replacing Patients) */}
              <div 
                onClick={() => navigate('/directory')}
                className="absolute bottom-16 left-20 bg-white rounded-xl p-4 shadow-xl transform rotate-8 hover:rotate-0 transition-all duration-500 hover:scale-105 group cursor-pointer border-2 border-indigo-200"
              >
                <div className="text-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <UserGroupIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-apollo-primary group-hover:text-indigo-600 transition-colors">
                    156
                  </div>
                  <div className="text-xs text-gray-600 font-medium">
                    Team
                  </div>
                </div>
                <div
                  className="absolute -bottom-1 -right-1 w-4 h-4 bg-pink-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.5s" }}
                ></div>
              </div>

              {/* Central Team Hub Badge */}
              <div 
                onClick={() => navigate('/directory')}
                className="hidden lg:block bg-white/10 backdrop-blur-sm rounded-full p-6 border border-blue-300/30 shadow-xl cursor-pointer hover:bg-white/20 transition-all duration-300"
              >
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <UserGroupIcon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-white font-bold text-lg">Team Hub</div>
                  <div className="text-blue-200 text-sm">Connected</div>
                </div>
              </div>

              {/* Floating Animation Elements */}
              <div className="absolute top-32 left-32 w-3 h-3 bg-blue-400/60 rounded-full animate-pulse"></div>
              <div
                className="absolute bottom-32 right-32 w-4 h-4 bg-emerald-400/60 rounded-full animate-bounce"
                style={{ animationDelay: "1s" }}
              ></div>
              <div
                className="absolute top-48 right-24 w-2 h-2 bg-purple-400/60 rounded-full animate-ping"
                style={{ animationDelay: "2s" }}
              ></div>
            </div>
          </div>
        </div>
      </section>

      {/* Healthcare Services Section */}
      {/* Platform Services Showcase - Screenshot Concept */}
      <section className="py-8 pt-0 pb-0 bg-apollo-gray relative overflow-hidden">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #3b82f6 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, #6366f1 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        {/* Floating Geometric Shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-16 left-12 w-8 h-8 border-2 border-apollo-accent/20 rounded-lg rotate-45 animate-spin-slow"></div>
          <div className="absolute top-32 right-20 w-6 h-6 bg-apollo-primary/10 rounded-full animate-bounce"></div>
          <div className="absolute bottom-24 left-1/4 w-4 h-16 bg-gradient-to-t from-apollo-secondary/20 to-transparent rounded-full animate-pulse"></div>
          <div className="absolute bottom-16 right-16 w-12 h-12 border border-apollo-accent/30 rounded-full animate-ping"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Services Grid - Single Row Layout */}
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 lg:gap-8 py-6">
            
            {/* Training */}
            <div
              onClick={() => navigate("/training")}
              className="w-36 sm:w-40 md:w-44 lg:w-[170px] group cursor-pointer transform -rotate-3 hover:rotate-0 transition-all duration-700 hover:scale-110"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-500">
                {/* Mini Screenshot Header */}
                <div className="bg-gradient-to-r from-apollo-primary to-apollo-accent p-2 relative">
                  <div className="flex items-center space-x-1 mb-1">
                    <div className="w-1.5 h-1.5 bg-white/80 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-white/60 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-white/40 rounded-full"></div>
                  </div>
                  <div className="text-white text-xs font-mono opacity-80">training.apollo.com</div>
                  <div className="absolute top-1 right-2 text-lg group-hover:animate-bounce">🎓</div>
                </div>
                
                {/* Content Area */}
                <div className="p-4 bg-gradient-to-br from-white to-blue-50/30">
                  <div className="text-center">
                    <h3 className="text-sm font-bold text-apollo-primary mb-1 group-hover:text-apollo-accent transition-colors">
                      Training Hub
                    </h3>
                    <p className="text-xs text-gray-600 mb-2">Learn & Develop</p>
                    {/* Mini Progress Bars */}
                    <div className="space-y-1">
                      <div className="h-0.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-apollo-primary w-3/4 rounded-full animate-pulse"></div>
                      </div>
                      <div className="h-0.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-apollo-accent w-1/2 rounded-full animate-pulse delay-200"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Events */}
            <div
              onClick={() => navigate("/events")}
              className="w-36 sm:w-40 md:w-44 lg:w-[170px] group cursor-pointer transform rotate-2 hover:rotate-0 transition-all duration-700 hover:scale-110"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-500">
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-2 relative">
                  <div className="flex items-center space-x-1 mb-1">
                    <div className="w-1.5 h-1.5 bg-white/80 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-white/60 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-white/40 rounded-full"></div>
                  </div>
                  <div className="text-white text-xs font-mono opacity-80">events.apollo.com</div>
                  <div className="absolute top-1 right-2 text-lg group-hover:animate-spin">📅</div>
                </div>
                
                <div className="p-4 bg-gradient-to-br from-white to-purple-50/30">
                  <div className="text-center">
                    <h3 className="text-sm font-bold text-purple-600 mb-1 group-hover:text-pink-600 transition-colors">
                      Events
                    </h3>
                    <p className="text-xs text-gray-600 mb-2">Calendar & Activities</p>
                    {/* Mini Calendar Grid */}
                    <div className="grid grid-cols-7 gap-0.5 text-xs">
                      {[...Array(7)].map((_, i) => (
                        <div key={i} className={`w-2 h-2 rounded-sm ${i === 3 ? 'bg-purple-400' : 'bg-gray-200'} ${i === 5 ? 'bg-pink-400' : ''}`}></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Forums */}
            <div
              onClick={() => navigate("/forums")}
              className="w-36 sm:w-40 md:w-44 lg:w-[170px] group cursor-pointer transform -rotate-1 hover:rotate-0 transition-all duration-700 hover:scale-110"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-500">
                <div className="bg-gradient-to-r from-green-500 to-teal-500 p-2 relative">
                  <div className="flex items-center space-x-1 mb-1">
                    <div className="w-1.5 h-1.5 bg-white/80 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-white/60 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-white/40 rounded-full"></div>
                  </div>
                  <div className="text-white text-xs font-mono opacity-80">forums.apollo.com</div>
                  <div className="absolute top-1 right-2 text-lg group-hover:animate-pulse">💬</div>
                </div>
                
                <div className="p-4 bg-gradient-to-br from-white to-green-50/30">
                  <div className="text-center">
                    <h3 className="text-sm font-bold text-green-600 mb-1 group-hover:text-teal-600 transition-colors">
                      Forums
                    </h3>
                    <p className="text-xs text-gray-600 mb-2">Community Discussions</p>
                    {/* Mini Chat Bubbles */}
                    <div className="space-y-1">
                      <div className="flex justify-start">
                        <div className="w-6 h-1.5 bg-green-200 rounded-full"></div>
                      </div>
                      <div className="flex justify-end">
                        <div className="w-4 h-1.5 bg-teal-200 rounded-full"></div>
                      </div>
                      <div className="flex justify-start">
                        <div className="w-8 h-1.5 bg-green-200 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Support */}
            <div 
              onClick={() => navigate("/support")}
              className="w-36 sm:w-40 md:w-44 lg:w-[170px] group cursor-pointer transform rotate-3 hover:rotate-0 transition-all duration-700 hover:scale-110"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-500">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-2 relative">
                  <div className="flex items-center space-x-1 mb-1">
                    <div className="w-1.5 h-1.5 bg-white/80 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-white/60 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-white/40 rounded-full"></div>
                  </div>
                  <div className="text-white text-xs font-mono opacity-80">support.apollo.com</div>
                  <div className="absolute top-1 right-2 text-lg group-hover:animate-bounce">❓</div>
                </div>
                
                <div className="p-4 bg-gradient-to-br from-white to-amber-50/30">
                  <div className="text-center">
                    <h3 className="text-sm font-bold text-amber-600 mb-1 group-hover:text-orange-600 transition-colors">
                      Support
                    </h3>
                    <p className="text-xs text-gray-600 mb-2">Help & Assistance</p>
                    {/* Mini Ticket Status */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500 text-xs">Tickets</span>
                        <span className="text-green-500">●</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500 text-xs">Response</span>
                        <span className="text-amber-500 animate-pulse">●</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payroll */}
            <div
              onClick={() => navigate("/payroll")}
              className="w-36 sm:w-40 md:w-44 lg:w-[170px] group cursor-pointer transform -rotate-2 hover:rotate-0 transition-all duration-700 hover:scale-110"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-500">
                <div className="bg-gradient-to-r from-slate-600 to-gray-700 p-2 relative">
                  <div className="flex items-center space-x-1 mb-1">
                    <div className="w-1.5 h-1.5 bg-white/80 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-white/60 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-white/40 rounded-full"></div>
                  </div>
                  <div className="text-white text-xs font-mono opacity-80">payroll.apollo.com</div>
                  <div className="absolute top-1 right-2 text-lg group-hover:animate-pulse">💳</div>
                </div>
                
                <div className="p-4 bg-gradient-to-br from-white to-slate-50/30">
                  <div className="text-center">
                    <h3 className="text-sm font-bold text-slate-600 mb-1 group-hover:text-gray-700 transition-colors">
                      Payroll
                    </h3>
                    <p className="text-xs text-gray-600 mb-2">Salary & Benefits</p>
                    {/* Mini Chart Bars */}
                    <div className="flex items-end justify-center space-x-1">
                      <div className="w-1.5 h-3 bg-slate-300 rounded-sm"></div>
                      <div className="w-1.5 h-4 bg-slate-400 rounded-sm animate-pulse"></div>
                      <div className="w-1.5 h-2 bg-slate-300 rounded-sm"></div>
                      <div className="w-1.5 h-3.5 bg-slate-400 rounded-sm"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Attendance */}
            <div
              onClick={() => navigate("/attendance")}
              className="w-36 sm:w-40 md:w-44 lg:w-[170px] group cursor-pointer transform rotate-1 hover:rotate-0 transition-all duration-700 hover:scale-110"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-500">
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-2 relative">
                  <div className="flex items-center space-x-1 mb-1">
                    <div className="w-1.5 h-1.5 bg-white/80 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-white/60 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-white/40 rounded-full"></div>
                  </div>
                  <div className="text-white text-xs font-mono opacity-80">attendance.apollo.com</div>
                  <div className="absolute top-1 right-2 text-lg group-hover:animate-spin">📊</div>
                </div>
                
                <div className="p-4 bg-gradient-to-br from-white to-indigo-50/30">
                  <div className="text-center">
                    <h3 className="text-sm font-bold text-indigo-600 mb-1 group-hover:text-purple-600 transition-colors">
                      Attendance
                    </h3>
                    <p className="text-xs text-gray-600 mb-2">Time & Tracking</p>
                    {/* Mini Clock */}
                    <div className="relative w-6 h-6 mx-auto border-2 border-indigo-300 rounded-full">
                      <div className="absolute top-0.5 left-1/2 w-0.5 h-1.5 bg-indigo-500 transform -translate-x-1/2 origin-bottom animate-pulse"></div>
                      <div className="absolute top-1/2 left-0.5 w-1.5 h-0.5 bg-indigo-400 transform -translate-y-1/2"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* Meet Your New Colleagues Section */}
      <section className="py-8 pb-2 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-apollo-accent/10 to-blue-600/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-br from-emerald-500/10 to-teal-600/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Beautiful Header Section */}
          <div className="flex items-center justify-between mb-5">
            <div className="text-left">
              <div className="relative">
                <h3 className="text-2xl md:text-2xl font-extrabold text-transparent bg-gradient-to-r from-apollo-primary via-apollo-accent to-blue-600 bg-clip-text leading-tight">
                  Meet Your New Colleagues
                </h3>
                <div className="absolute -bottom-2 left-0 w-16 h-0.5 bg-gradient-to-r from-apollo-accent to-blue-600 rounded-full"></div>
              </div>
            </div>
            <div className="flex flex-col items-end">
              <button
                onClick={() => navigate("/directory")}
                className="group relative bg-gradient-to-r from-apollo-accent to-blue-600 hover:from-apollo-primary hover:to-apollo-accent text-white px-4 py-2 rounded-xl font-bold transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-2 hover:scale-105 flex items-center space-x-2"
              >
                <span className="text-sm">See All</span>
                <ChevronRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {newColleagues.map((colleague, index) => (
              <div key={colleague.id} className="group relative">
                {/* Main Card */}
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 group-hover:border-apollo-accent/30 transform hover:-translate-y-2">
                  {/* Header with gradient */}
                  <div className="relative h-24 bg-gradient-to-r from-apollo-accent to-blue-600 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-apollo-accent/80 to-blue-600/80"></div>
                    <div className="absolute top-4 right-4">
                      <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">
                          NEW
                        </span>
                      </div>
                    </div>
                    {/* Floating decorations */}
                    <div className="absolute -top-4 -left-4 w-8 h-8 bg-white/10 rounded-full"></div>
                    <div className="absolute top-2 right-12 w-6 h-6 bg-white/10 rounded-full"></div>
                  </div>

                  {/* Profile Image */}
                  <div className="relative -mt-12 flex justify-center">
                    <div className="relative">
                      <img
                        src={colleague.image}
                        alt={colleague.name}
                        className="w-24 h-24 rounded-2xl object-cover border-4 border-white shadow-lg group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-400 rounded-full border-4 border-white flex items-center justify-center">
                        <div className="w-3 h-3 bg-green-600 rounded-full animate-pulse"></div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 pt-4 text-center">
                    <h4 className="text-xl font-bold text-apollo-primary mb-2 group-hover:text-apollo-accent transition-colors">
                      {colleague.name}
                    </h4>
                    <p className="text-apollo-accent font-semibold mb-1">
                      {colleague.position}
                    </p>
                    <p className="text-gray-600 text-sm mb-4">
                      {colleague.department}
                    </p>

                    {/* Join Date Badge */}
                    <div className="inline-flex items-center bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-full px-4 py-2 mb-6">
                      <SparklesIcon className="w-4 h-4 text-emerald-500 mr-2" />
                      <span className="text-emerald-700 text-sm font-medium">
                        Joined {colleague.joinDate}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3">
                      <button className="w-full bg-gradient-to-r from-apollo-accent to-blue-600 text-white py-3 px-6 rounded-xl hover:from-apollo-accent/90 hover:to-blue-600/90 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5">
                        <div className="flex items-center justify-center space-x-2">
                          <UserGroupIcon className="w-4 h-4" />
                          <span>Connect</span>
                        </div>
                      </button>
                      <button
                        onClick={() =>
                          navigate(`/directory/employee/${colleague.id}`)
                        }
                        className="w-full border-2 border-apollo-accent/20 text-apollo-accent py-2 px-6 rounded-xl hover:bg-apollo-accent/5 transition-all duration-200 font-medium"
                      >
                        View Profile
                      </button>
                    </div>
                  </div>
                </div>

                {/* Floating Animation Elements */}
                <div className="absolute -top-2 -right-2 w-4 h-4 bg-apollo-accent/20 rounded-full animate-ping"></div>
                <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-emerald-400/20 rounded-full animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Shortcuts Section */}
      <section className="py-8 pb-0 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Quick Shortcuts */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div className="text-left">
                  <div className="relative">
                    <h2 className="text-xl md:text-2xl font-extrabold text-transparent bg-gradient-to-r from-apollo-primary via-apollo-accent to-blue-600 bg-clip-text leading-tight">
                      Quick Shortcuts
                    </h2>
                    <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-apollo-accent to-blue-600 rounded-full"></div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3 mb-6">
                {shortcuts.slice(0, 8).map((shortcut) => (
                  <div
                    key={shortcut.id}
                    onClick={() => openShortcut(shortcut.url)}
                    className="group bg-white rounded-xl p-4 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 hover:border-apollo-accent/30 transform hover:-translate-y-1 relative"
                  >
                    <div className="text-center">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2 group-hover:bg-apollo-accent/10 transition-colors text-lg">
                        {shortcut.icon}
                      </div>
                      <h3 className="font-medium text-gray-900 group-hover:text-apollo-accent transition-colors text-xs">
                        {shortcut.name}
                      </h3>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeShortcut(shortcut.id);
                      }}
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-1 bg-red-100 hover:bg-red-200 rounded-full transition-all"
                      title="Remove shortcut"
                    >
                      <XMarkIcon className="w-2 h-2 text-red-500" />
                    </button>
                  </div>
                ))}

                {/* Placeholder buttons for empty slots */}
                {Array.from({ length: Math.max(0, 8 - shortcuts.length) }).map(
                  (_, index) => (
                    <div
                      key={`placeholder-${index}`}
                      onClick={() => setShowAddShortcut(true)}
                      className="group bg-gradient-to-br from-apollo-accent/5 to-blue-600/5 rounded-xl p-4 border-2 border-dashed border-apollo-accent/20 hover:border-apollo-accent/40 transition-all duration-300 cursor-pointer flex items-center justify-center transform hover:-translate-y-1"
                    >
                      <div className="text-center">
                        <div className="w-8 h-8 bg-apollo-accent/10 rounded-full flex items-center justify-center mx-auto mb-2 group-hover:bg-apollo-accent/20 transition-colors">
                          <PlusIcon className="w-4 h-4 text-apollo-accent" />
                        </div>
                        <h3 className="font-medium text-apollo-accent text-xs">
                          Add
                        </h3>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Celebrations Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-left">
                  <div className="relative">
                    <h2 className="text-xl md:text-2xl font-extrabold text-transparent bg-gradient-to-r from-apollo-primary via-apollo-accent to-blue-600 bg-clip-text leading-tight">
                      Today's Celebrations
                    </h2>
                    <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-apollo-accent to-blue-600 rounded-full"></div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 shadow-md hover:shadow-lg transition-shadow duration-300 h-[230px] flex flex-col mb-2">
                {/* Today's Celebration Events */}
                <div className="p-4 flex-1">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-apollo-accent/5 to-blue-600/5 rounded-lg hover:from-apollo-accent/10 hover:to-blue-600/10 transition-all duration-300">
                      <div className="relative">
                        <img
                          src="https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=40&h=40&fit=crop&crop=face"
                          alt="Birthday person"
                          className="w-10 h-10 rounded-full object-cover ring-2 ring-apollo-accent/20"
                        />
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-apollo-accent rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">🎂</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 text-sm">
                          Dr. Priya Sharma
                        </h4>
                        <p className="text-xs text-gray-600">
                          Cardiology Dept.
                        </p>
                      </div>
                      <button className="px-3 py-1 bg-apollo-accent text-white rounded-md text-xs font-medium hover:bg-apollo-primary transition-colors">
                        Wish 🎁
                      </button>
                    </div>

                    <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-apollo-primary/5 to-apollo-secondary/5 rounded-lg hover:from-apollo-primary/10 hover:to-apollo-secondary/10 transition-all duration-300">
                      <div className="relative">
                        <img
                          src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face"
                          alt="Birthday person"
                          className="w-10 h-10 rounded-full object-cover ring-2 ring-apollo-primary/20"
                        />
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-apollo-primary rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">🎉</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 text-sm">
                          Rajesh Kumar
                        </h4>
                        <p className="text-xs text-gray-600">IT Department</p>
                      </div>
                      <button className="px-3 py-1 bg-apollo-primary text-white rounded-md text-xs font-medium hover:bg-apollo-secondary transition-colors">
                        Say Hi 👋
                      </button>
                    </div>
                  </div>
                </div>

                {/* This Week's Birthdays - Footer */}
                <div className="border-t border-gray-100 p-3 bg-gradient-to-r from-gray-50 to-apollo-accent/5 rounded-b-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CalendarDaysIcon className="w-4 h-4 text-apollo-accent" />
                      <span className="text-xs font-medium text-gray-700">
                        This Week
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <div className="text-center">
                        <div className="text-xs font-medium text-gray-700">
                          Wed
                        </div>
                        <div className="text-xs text-gray-600">Anita M.</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs font-medium text-gray-700">
                          Thu
                        </div>
                        <div className="text-xs text-gray-600">Vikram S.</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs font-medium text-gray-700">
                          Fri
                        </div>
                        <div className="text-xs text-gray-600">Kavya R.</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Add Shortcut Modal/Form */}
          {showAddShortcut && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Add New Shortcut
                  </h3>
                  <button
                    onClick={() => setShowAddShortcut(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shortcut Name
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., My Portal"
                      value={newShortcut.name}
                      onChange={(e) =>
                        setNewShortcut({ ...newShortcut, name: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-apollo-accent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Website URL
                    </label>
                    <input
                      type="url"
                      placeholder="https://example.com"
                      value={newShortcut.url}
                      onChange={(e) =>
                        setNewShortcut({ ...newShortcut, url: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-apollo-accent"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Icon
                      </label>
                      <input
                        type="text"
                        placeholder="🔗"
                        value={newShortcut.icon}
                        onChange={(e) =>
                          setNewShortcut({
                            ...newShortcut,
                            icon: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-apollo-accent"
                        maxLength="2"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        value={newShortcut.category}
                        onChange={(e) =>
                          setNewShortcut({
                            ...newShortcut,
                            category: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-apollo-accent"
                      >
                        <option value="General">General</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Communication">Communication</option>
                        <option value="Learning">Learning</option>
                        <option value="Tools">Tools</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={addShortcut}
                    disabled={
                      !newShortcut.name.trim() || !newShortcut.url.trim()
                    }
                    className="flex-1 bg-apollo-accent text-white py-2 px-4 rounded-lg font-semibold hover:bg-apollo-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Shortcut
                  </button>
                  <button
                    onClick={() => setShowAddShortcut(false)}
                    className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Enhanced Statistics Section */}
      <section
        id="portal-stats"
        className="py-8 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden"
      >
        {/* Modern Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute top-10 right-20 w-96 h-96 bg-gradient-to-br from-apollo-accent/10 to-blue-600/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-20 w-80 h-80 bg-gradient-to-br from-emerald-500/10 to-teal-600/10 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-br from-purple-500/5 to-pink-600/5 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-0">
          <div className="flex items-center justify-between mb-5">
            <div className="text-left">
              <div className="relative">
                <h3 className="text-2xl md:text-2xl font-extrabold text-transparent bg-gradient-to-r from-apollo-primary via-apollo-accent to-blue-600 bg-clip-text leading-tight">
                  Elysium Impact Metrics
                </h3>
                <div className="absolute -bottom-2 left-0 w-16 h-0.5 bg-gradient-to-r from-apollo-accent to-blue-600 rounded-full"></div>
              </div>
            </div>
            <div className="flex flex-col items-end">
              <button className="group relative bg-gradient-to-r from-apollo-accent to-blue-600 hover:from-apollo-primary hover:to-apollo-accent text-white px-4 py-2 rounded-xl font-bold transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-2 hover:scale-105 flex items-center space-x-2">
                <span className="text-sm">See All</span>
                <ChevronRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {portalStats.map((stat, index) => (
              <div key={index} className="group relative">
                {/* Animated Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-lg group-hover:shadow-2xl transition-all duration-500 transform group-hover:-translate-y-3 group-hover:scale-105"></div>

                {/* Content */}
                <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-6 border border-gray-100 group-hover:border-apollo-accent/30 transition-all duration-300">
                  {/* Icon with floating effect */}
                  <div className="relative mb-5">
                    <div
                      className={`w-14 h-14 ${stat.color} rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300 shadow-lg group-hover:shadow-xl`}
                    >
                      <stat.icon className="w-7 h-7 text-white" />
                    </div>
                    {/* Floating indicator */}
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full flex items-center justify-center shadow-md">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="text-3xl font-black text-apollo-primary mb-2 group-hover:scale-110 transition-transform duration-300">
                      {stat.value}
                    </div>
                    <div className="text-base font-bold text-apollo-secondary mb-3">
                      {stat.label}
                    </div>
                  </div>

                  {/* Bottom accent line */}
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-apollo-accent to-blue-500 rounded-full group-hover:w-20 transition-all duration-300"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Centres of Digital Excellence Section */}
      <section
        id="digital-excellence"
        className="py-8 pb-2 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-20 w-96 h-96 bg-apollo-accent rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-apollo-primary rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center justify-between mb-8">
            <div className="text-left">
              <div className="relative">
                <h2 className="text-2xl md:text-2xl font-extrabold text-transparent bg-gradient-to-r from-apollo-primary via-apollo-accent to-blue-600 bg-clip-text leading-tight">
                  Centres of Digital Excellence
                </h2>
                <div className="absolute -bottom-2 left-0 w-16 h-0.5 bg-gradient-to-r from-apollo-accent to-blue-600 rounded-full"></div>
              </div>
              <p className="text-gray-600 mt-4 text-lg">
                Specialized digital solutions across workplace domains
              </p>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
            {/* Text Content Column */}
            <div className="space-y-12">
              <div className="group">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <BeakerIcon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-apollo-primary mb-3">
                      AI-Powered Analytics
                    </h3>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      Advanced data insights and predictive analytics to drive
                      smarter decision-making across all departments.
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 ml-20">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-apollo-accent">
                        95%
                      </div>
                      <div className="text-sm text-gray-600">Accuracy Rate</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-apollo-accent">
                        50ms
                      </div>
                      <div className="text-sm text-gray-600">Response Time</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="group">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <UserGroupIcon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-apollo-primary mb-3">
                      Digital Wellness
                    </h3>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      Comprehensive healthcare wellbeing platform with health
                      tracking and mental wellness resources.
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 ml-20">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-apollo-accent">
                        85%
                      </div>
                      <div className="text-sm text-gray-600">Engagement</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-apollo-accent">
                        40+
                      </div>
                      <div className="text-sm text-gray-600">Programs</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Image Column */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="relative group overflow-hidden rounded-2xl shadow-xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
                    <img
                      src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=300&fit=crop"
                      alt="AI Analytics Dashboard"
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-apollo-primary/80 to-transparent"></div>
                  </div>

                  <div className="relative group overflow-hidden rounded-2xl shadow-xl transform -rotate-2 hover:rotate-0 transition-transform duration-500">
                    <img
                      src="https://images.unsplash.com/photo-1563986768609-322da13575f3?w=400&h=300&fit=crop"
                      alt="Digital Wellness Platform"
                      className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-purple-600/80 to-transparent"></div>
                  </div>
                </div>

                <div className="space-y-6 mt-12">
                  <div className="relative group overflow-hidden rounded-2xl shadow-xl transform -rotate-3 hover:rotate-0 transition-transform duration-500">
                    <img
                      src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=350&fit=crop"
                      alt="Global Team Collaboration"
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-green-600/80 to-transparent"></div>
                  </div>

                  <div className="relative group overflow-hidden rounded-2xl shadow-xl transform rotate-2 hover:rotate-0 transition-transform duration-500">
                    <img
                      src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=250&fit=crop"
                      alt="Digital Innovation"
                      className="w-full h-40 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-apollo-accent/80 to-transparent"></div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-apollo-accent to-blue-600 rounded-full flex items-center justify-center shadow-2xl">
                <SparklesIcon className="w-10 h-10 text-white" />
              </div>

              <div className="absolute -bottom-6 -left-6 w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-full flex items-center justify-center shadow-2xl">
                <TrophyIcon className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>

          {/* Section Divider */}
          <div className="flex justify-center mt-12">
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-apollo-accent/30 to-transparent"></div>
          </div>
        </div>
      </section>

      {/* Tasks, Documents, and Forms - Three Column Layout */}
      <section className="py-8 pb-0 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Tasks Column */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                {/* Tasks Header */}
                <div className="bg-gradient-to-r from-apollo-primary to-apollo-accent px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                        <ClockIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">
                          My Tasks
                        </h3>
                        <p className="text-blue-100 text-sm">
                          Daily priorities
                        </p>
                      </div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
                      <span className="text-white font-semibold text-sm">
                        {myTasks.length}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Tasks Content */}
                <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
                  {myTasks.map((task) => (
                    <div
                      key={task.id}
                      className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all duration-200 group"
                    >
                      <div className="flex items-start space-x-3">
                        <div
                          className={`w-5 h-5 rounded-full border-2 mt-0.5 ${
                            task.status === "In Progress"
                              ? "bg-apollo-accent border-apollo-accent"
                              : task.status === "Pending"
                              ? "border-orange-400"
                              : "border-gray-300"
                          }`}
                        ></div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 text-sm group-hover:text-apollo-accent transition-colors">
                            {task.title}
                          </h4>
                          <p className="text-gray-600 text-xs mt-1 line-clamp-2">
                            {task.description}
                          </p>
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <UserIcon className="w-3 h-3" />
                              <span>{task.assignedBy}</span>
                            </div>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                task.priority === "High"
                                  ? "bg-red-100 text-red-700"
                                  : task.priority === "Medium"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : "bg-green-100 text-green-700"
                              }`}
                            >
                              {task.priority}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tasks Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                  <button 
                    onClick={() => navigate('/tasks')}
                    className="w-full text-center text-apollo-accent hover:text-apollo-primary font-medium text-sm transition-colors"
                  >
                    View All Tasks →
                  </button>
                </div>
              </div>
            </div>

            {/* Documents Column */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                {/* Documents Header */}
                <div className="bg-gradient-to-r from-apollo-secondary to-apollo-accent px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">
                          Documents
                        </h3>
                        <p className="text-blue-100 text-sm">Recent files</p>
                      </div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
                      <span className="text-white font-semibold text-sm">
                        {recentDocs.length}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Documents Content */}
                <div className="p-6 space-y-3 max-h-96 overflow-y-auto">
                  {recentDocs.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center space-x-4 p-3 hover:bg-gray-50 rounded-lg transition-colors group cursor-pointer"
                    >
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          doc.type === "PDF"
                            ? "bg-red-100 text-red-600"
                            : doc.type === "Excel"
                            ? "bg-green-100 text-green-600"
                            : "bg-blue-100 text-blue-600"
                        }`}
                      >
                        {doc.type === "PDF" ? (
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : doc.type === "Excel" ? (
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                          </svg>
                        ) : (
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate group-hover:text-apollo-accent transition-colors">
                          {doc.name}
                        </h4>
                        <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                          <span>{doc.size}</span>
                          <span>•</span>
                          <span>{doc.lastModified}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Documents Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                  <button 
                    onClick={() => navigate('/documents')}
                    className="w-full text-center text-apollo-accent hover:text-apollo-primary font-medium text-sm transition-colors"
                  >
                    View All Documents →
                  </button>
                </div>
              </div>
            </div>

            {/* Forms Column */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                {/* Forms Header */}
                <div className="bg-gradient-to-r from-apollo-accent to-apollo-primary px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                          <path
                            fillRule="evenodd"
                            d="M4 5a2 2 0 012-2v1a2 2 0 002 2h4a2 2 0 002-2V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">
                          My Forms
                        </h3>
                        <p className="text-blue-100 text-sm">Submissions</p>
                      </div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
                      <span className="text-white font-semibold text-sm">
                        {myForms.length}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Forms Content */}
                <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
                  {myForms.map((form) => (
                    <div
                      key={form.id}
                      className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all duration-200 group"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3">
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              form.category === "HR"
                                ? "bg-blue-100 text-blue-600"
                                : form.category === "Facilities"
                                ? "bg-orange-100 text-orange-600"
                                : "bg-green-100 text-green-600"
                            }`}
                          >
                            {form.category === "HR"
                              ? "👥"
                              : form.category === "Facilities"
                              ? "🔧"
                              : "📚"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 text-sm group-hover:text-apollo-accent transition-colors line-clamp-2">
                              {form.name}
                            </h4>
                            <div className="flex items-center space-x-2 mt-2">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  form.category === "HR"
                                    ? "bg-blue-100 text-blue-700"
                                    : form.category === "Facilities"
                                    ? "bg-orange-100 text-orange-700"
                                    : "bg-green-100 text-green-700"
                                }`}
                              >
                                {form.category}
                              </span>
                            </div>
                          </div>
                        </div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                            form.status === "Draft"
                              ? "bg-gray-100 text-gray-700"
                              : form.status === "Submitted"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {form.status}
                        </span>
                      </div>
                      <div className="mt-3 text-xs text-gray-500">
                        Updated {form.lastUpdated}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Forms Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                  <button 
                    onClick={() => navigate('/forms')}
                    className="w-full text-center text-apollo-accent hover:text-apollo-primary font-medium text-sm transition-colors"
                  >
                    View All Forms →
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Section Divider */}
          <div className="flex justify-center mt-12">
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          </div>
        </div>
      </section>

      {/* Staff Insights & Engagement Section */}
      <section id="staff-engagement" className="py-8 pb-4 bg-apollo-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div className="text-left">
              <div className="relative">
                <h2 className="text-2xl md:text-2xl font-extrabold text-transparent bg-gradient-to-r from-apollo-primary via-apollo-accent to-blue-600 bg-clip-text leading-tight">
                  Staff Insights & Engagement
                </h2>
                <div className="absolute -bottom-2 left-0 w-16 h-0.5 bg-gradient-to-r from-apollo-accent to-blue-600 rounded-full"></div>
              </div>
              <p className="text-gray-600 mt-4 text-lg">
                Survey feedback, calendar access, and team engagement
              </p>
            </div>
          </div>

          <div className="">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Staff Pulse Survey */}
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-apollo-accent to-blue-600 rounded-xl flex items-center justify-center mr-4">
                    <ChartBarIcon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-apollo-primary">
                    Wellness Pulse
                  </h3>
                </div>
                <div className="relative group overflow-hidden rounded-xl shadow-md mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop"
                    alt="Team Discussion"
                    className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end">
                    <div className="p-4 text-white">
                      <h4 className="font-bold text-sm">Voice Your Opinion</h4>
                      <p className="text-xs opacity-90">
                        Shape workplace policies
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900">
                      {pollsData[currentPollIndex].question}
                    </h4>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() =>
                          setCurrentPollIndex((prev) =>
                            prev > 0 ? prev - 1 : pollsData.length - 1
                          )
                        }
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                      >
                        <ChevronLeftIcon className="w-4 h-4 text-gray-600" />
                      </button>
                      <span className="text-xs text-gray-500">
                        {currentPollIndex + 1}/{pollsData.length}
                      </span>
                      <button
                        onClick={() =>
                          setCurrentPollIndex((prev) =>
                            prev < pollsData.length - 1 ? prev + 1 : 0
                          )
                        }
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                      >
                        <ChevronRightIcon className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {pollsData[currentPollIndex].options.map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center space-x-3"
                      >
                        <input
                          type="radio"
                          name={`poll-${pollsData[currentPollIndex].id}`}
                          value={option.value}
                          checked={pollAnswer === option.value}
                          onChange={(e) => setPollAnswer(e.target.value)}
                          className="text-apollo-accent"
                        />
                        <span className="text-gray-700">{option.label}</span>
                      </label>
                    ))}
                  </div>
                  <button className="w-full bg-gradient-to-r from-apollo-accent to-blue-600 text-white py-3 px-4 rounded-lg mt-4 hover:from-apollo-primary hover:to-apollo-accent transition-all duration-300 font-semibold">
                    Submit Feedback
                  </button>
                </div>
              </div>

              {/* Pay & Holidays Calendar */}
              <div className="lg:col-span-2 bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-emerald-700 rounded-xl flex items-center justify-center mr-4">
                    <CurrencyDollarIcon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-apollo-primary">
                    CALENDAR
                  </h3>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                        <ChevronLeftIcon className="w-5 h-5 text-apollo-primary" />
                      </button>
                      <h4 className="font-bold text-apollo-primary">
                        July 2025
                      </h4>
                      <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                        <ChevronRightIcon className="w-5 h-5 text-apollo-primary" />
                      </button>
                    </div>

                    <div className="grid grid-cols-7 gap-1 text-center text-sm mb-4">
                      <div className="font-medium text-gray-500 p-2">S</div>
                      <div className="font-medium text-gray-500 p-2">M</div>
                      <div className="font-medium text-gray-500 p-2">T</div>
                      <div className="font-medium text-gray-500 p-2">W</div>
                      <div className="font-medium text-gray-500 p-2">T</div>
                      <div className="font-medium text-gray-500 p-2">F</div>
                      <div className="font-medium text-gray-500 p-2">S</div>

                      {/* Calendar days */}
                      <div className="p-2"></div>
                      <div className="p-2"></div>
                      <div className="p-2">1</div>
                      <div className="p-2">2</div>
                      <div className="p-2">3</div>
                      <div className="p-2">4</div>
                      <div className="p-2">5</div>

                      <div className="p-2">6</div>
                      <div className="p-2">7</div>
                      <div className="p-2">8</div>
                      <div className="p-2">9</div>
                      <div className="p-2">10</div>
                      <div className="p-2">11</div>
                      <div className="p-2">12</div>

                      <div className="p-2">13</div>
                      <div className="p-2">14</div>
                      <div className="p-2 bg-red-100 rounded text-red-700 font-semibold">
                        15
                      </div>
                      <div className="p-2">16</div>
                      <div className="p-2">17</div>
                      <div className="p-2">18</div>
                      <div className="p-2">19</div>

                      <div className="p-2">20</div>
                      <div className="p-2">21</div>
                      <div className="p-2">22</div>
                      <div className="p-2">23</div>
                      <div className="p-2">24</div>
                      <div className="p-2">25</div>
                      <div className="p-2">26</div>

                      <div className="p-2">27</div>
                      <div className="p-2">28</div>
                      <div className="p-2">29</div>
                      <div className="p-2 bg-orange-100 rounded text-orange-700 font-semibold">
                        30
                      </div>
                      <div className="p-2 bg-blue-100 rounded text-blue-700 font-semibold">
                        31
                      </div>
                      <div className="p-2"></div>
                      <div className="p-2"></div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-100">
                      <div>
                        <div className="font-semibold text-red-900">15 AUG</div>
                        <div className="text-sm text-red-700">
                          Independence Day
                        </div>
                      </div>
                      <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        National
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-orange-50 rounded-xl border border-orange-100">
                      <div>
                        <div className="font-semibold text-orange-900">
                          30 JUL
                        </div>
                        <div className="text-sm text-orange-700">Mawlid</div>
                      </div>
                      <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        Mumbai
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-100">
                      <div>
                        <div className="font-semibold text-blue-900">
                          Leave applied
                        </div>
                        <div className="text-sm text-blue-700">
                          Summer vacation
                        </div>
                      </div>
                      <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        Approved
                      </span>
                    </div>

                    {/* View All Events Button */}
                    <button
                      onClick={() => navigate("/events")}
                      className="w-full bg-gradient-to-r from-apollo-accent to-blue-600 text-white py-3 px-4 rounded-lg mt-4 hover:from-apollo-primary hover:to-apollo-accent transition-all duration-300 font-semibold flex items-center justify-center space-x-2"
                    >
                      <CalendarDaysIcon className="w-5 h-5" />
                      <span>View All Events</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Welcome to Elysium Section - Redesigned Left/Right Layout */}
      <section className="py-20 bg-gradient-to-br from-apollo-primary via-apollo-secondary to-apollo-accent text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-cyan-300 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-purple-300 rounded-full blur-3xl"></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-4 h-4 bg-cyan-300 rounded-full animate-pulse"></div>
          <div
            className="absolute top-40 right-32 w-6 h-6 bg-purple-300 rounded-lg rotate-45 animate-bounce"
            style={{ animationDelay: "1s" }}
          ></div>
          <div
            className="absolute bottom-32 left-1/4 w-3 h-3 bg-white rounded-full animate-ping"
            style={{ animationDelay: "2s" }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center min-h-[600px]">
            {/* Left Side - Main Content */}
            <div className="space-y-8">
              {/* Welcome Header */}
              <div className="space-y-6">
                <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                  <SparklesIcon className="w-4 h-4 text-cyan-300 mr-2" />
                  <span className="text-white font-medium text-sm">
                    Apollo Digital Platform
                  </span>
                </div>

                <h1 className="text-4xl md:text-6xl font-black leading-tight">
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-200 to-purple-200">
                    Welcome to
                  </span>
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 via-white to-purple-200 relative">
                    Elysium
                    <div className="absolute -top-4 -right-8 w-12 h-12 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-2xl rotate-12 animate-bounce opacity-60"></div>
                  </span>
                </h1>
              </div>

              {/* Description */}
              <div className="space-y-6">
                <p className="text-xl md:text-2xl text-blue-100 leading-relaxed">
                  Your Gateway to{" "}
                  <span className="text-white font-bold">
                    Infinite Resources
                  </span>{" "}
                  to help you
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-cyan-300/20 group hover:bg-white/10 transition-all">
                    <RocketLaunchIcon className="w-8 h-8 text-cyan-300 mb-2 group-hover:scale-110 transition-transform" />
                    <h3 className="text-cyan-300 font-bold text-lg">Grow</h3>
                    <p className="text-blue-100 text-sm">Unlimited learning</p>
                  </div>

                  <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-purple-300/20 group hover:bg-white/10 transition-all">
                    <TrophyIcon className="w-8 h-8 text-purple-300 mb-2 group-hover:scale-110 transition-transform" />
                    <h3 className="text-purple-300 font-bold text-lg">
                      Get Recognition
                    </h3>
                    <p className="text-blue-100 text-sm">
                      Showcase achievements
                    </p>
                  </div>

                  <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/20 group hover:bg-white/10 transition-all">
                    <ChatBubbleLeftRightIcon className="w-8 h-8 text-white mb-2 group-hover:scale-110 transition-transform" />
                    <h3 className="text-white font-bold text-lg">Be Heard</h3>
                    <p className="text-blue-100 text-sm">Connect & share</p>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button className="flex-1 bg-gradient-to-r from-white to-gray-100 text-apollo-primary py-4 px-8 rounded-xl font-bold text-lg hover:from-gray-50 hover:to-white transition-all duration-300 flex items-center justify-center space-x-3 group shadow-xl">
                  <UserIcon className="w-6 h-6" />
                  <span>Login with SSO</span>
                  <ChevronRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>

                <button className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white py-4 px-8 rounded-xl font-bold text-lg hover:bg-white/20 transition-all duration-300 flex items-center justify-center space-x-3 group">
                  <BeakerIcon className="w-6 h-6" />
                  <span>Explore Features</span>
                </button>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center space-x-6 pt-6 border-t border-white/20">
                <div className="flex items-center space-x-2">
                  <ShieldCheckIcon className="w-5 h-5 text-green-300" />
                  <span className="text-white text-sm font-medium">
                    Enterprise Security
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <GlobeAltIcon className="w-5 h-5 text-blue-300" />
                  <span className="text-white text-sm font-medium">
                    Global Access
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <ClockIcon className="w-5 h-5 text-purple-300" />
                  <span className="text-white text-sm font-medium">
                    24/7 Support
                  </span>
                </div>
              </div>
            </div>

            {/* Right Side - Visual Elements & Login */}
            <div className="relative lg:ml-8">
              <div className="relative">
                {/* Main Login Card */}
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 shadow-2xl relative z-10 transform hover:scale-105 transition-all duration-500">
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                      <ShieldCheckIcon className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">
                      Secure Access Portal
                    </h3>
                    <p className="text-blue-100">
                      Enterprise-grade authentication
                    </p>
                  </div>

                  {/* Login Form Area */}
                  <div className="space-y-6">
                    <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-white font-semibold">
                          SSO Login
                        </span>
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                          <div
                            className="w-2 h-2 bg-green-400 rounded-full animate-pulse"
                            style={{ animationDelay: "0.2s" }}
                          ></div>
                          <div
                            className="w-2 h-2 bg-green-400 rounded-full animate-pulse"
                            style={{ animationDelay: "0.4s" }}
                          ></div>
                        </div>
                      </div>
                      <p className="text-blue-100 text-sm">
                        Single sign-on ready
                      </p>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-xl p-4 text-center border border-white/10">
                        <div className="text-2xl font-bold text-cyan-300">
                          25K+
                        </div>
                        <div className="text-xs text-blue-200">
                          Active Users
                        </div>
                      </div>
                      <div className="bg-white/5 rounded-xl p-4 text-center border border-white/10">
                        <div className="text-2xl font-bold text-purple-300">
                          99.9%
                        </div>
                        <div className="text-xs text-blue-200">Uptime</div>
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div className="text-center">
                      <p className="text-xs text-blue-200 mb-3">
                        Connect with Apollo network worldwide
                      </p>
                      <div className="flex justify-center space-x-4">
                        <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                          <GlobeAltIcon className="w-4 h-4 text-white" />
                        </div>
                        <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                          <UserGroupIcon className="w-4 h-4 text-white" />
                        </div>
                        <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                          <ChatBubbleLeftRightIcon className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Cards */}
                <div className="absolute -top-6 -left-6 bg-gradient-to-r from-cyan-400 to-cyan-600 rounded-xl p-4 shadow-xl transform -rotate-6 hover:rotate-0 transition-transform duration-500">
                  <div className="flex items-center space-x-2 text-white">
                    <AcademicCapIcon className="w-5 h-5" />
                    <div>
                      <div className="text-xs font-bold">Learning Hub</div>
                      <div className="text-xs opacity-90">500+ Courses</div>
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-purple-500 to-purple-700 rounded-xl p-4 shadow-xl transform rotate-6 hover:rotate-0 transition-transform duration-500">
                  <div className="flex items-center space-x-2 text-white">
                    <ChartBarIcon className="w-5 h-5" />
                    <div>
                      <div className="text-xs font-bold">Analytics</div>
                      <div className="text-xs opacity-90">Real-time Data</div>
                    </div>
                  </div>
                </div>

                <div className="absolute top-1/3 -right-8 bg-white/20 backdrop-blur-sm rounded-full p-3 shadow-xl animate-pulse">
                  <SparklesIcon className="w-6 h-6 text-white" />
                </div>

                {/* Background Decoration */}
                <div className="absolute inset-0 -z-10">
                  <div className="absolute top-10 left-10 w-32 h-32 bg-cyan-300/20 rounded-full blur-xl"></div>
                  <div className="absolute bottom-10 right-10 w-[170px] h-40 bg-purple-300/20 rounded-full blur-xl"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Section Divider */}
          <div className="flex justify-center mt-12">
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-apollo-accent/30 to-transparent"></div>
          </div>
        </div>
      </section>

      {/* Community Discussions Section */}
      <section className="py-8 pb-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div className="text-left">
              <div className="relative">
                <h2 className="text-2xl md:text-2xl font-extrabold text-transparent bg-gradient-to-r from-apollo-primary via-apollo-accent to-blue-600 bg-clip-text leading-tight">
                  Community Discussions
                </h2>
                <div className="absolute -bottom-2 left-0 w-16 h-0.5 bg-gradient-to-r from-apollo-accent to-blue-600 rounded-full"></div>
              </div>
              <p className="text-gray-600 mt-4 text-lg">
                Join meaningful conversations with colleagues across the Apollo
                network
              </p>
            </div>
            <div className="mt-6 md:mt-0">
              <button
                onClick={() => navigate("/forums")}
                className="bg-gradient-to-r from-apollo-accent to-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center space-x-2 group"
              >
                <span>See All</span>
                <ChevronRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Discussion Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {discussionPosts.slice(0, 3).map((post) => (
              <div
                key={post.id}
                onClick={() => navigate(`/forums/thread/${post.id}`)}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group hover:-translate-y-2 cursor-pointer"
              >
                {/* Post Image */}
                <div className="relative overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  {/* Author Info Overlay */}
                  <div className="absolute bottom-4 left-4 right-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 opacity-0 group-hover:opacity-100">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3">
                      <div className="text-sm font-semibold text-gray-900">
                        {post.author}
                      </div>
                      <div className="text-xs text-gray-600">
                        {post.department}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Post Content */}
                <div className="p-6">
                  <h3 className="text-lg font-bold text-apollo-primary mb-3 line-clamp-2 leading-tight group-hover:text-apollo-accent transition-colors">
                    {post.title}
                  </h3>

                  <div className="flex items-center justify-between">
                    {/* Engagement Actions */}
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => toggleLike(post.id)}
                        className="flex items-center space-x-2 group/like hover:bg-red-50 rounded-lg px-3 py-2 transition-all duration-200"
                      >
                        {likedPosts.has(post.id) ? (
                          <HeartSolidIcon className="w-5 h-5 text-red-500 group-hover/like:scale-110 transition-transform" />
                        ) : (
                          <HeartIcon className="w-5 h-5 text-gray-500 group-hover/like:text-red-500 group-hover/like:scale-110 transition-all" />
                        )}
                        <span
                          className={`text-sm font-medium ${
                            likedPosts.has(post.id)
                              ? "text-red-500"
                              : "text-gray-600"
                          }`}
                        >
                          {likedPosts.has(post.id)
                            ? post.likes + 1
                            : post.likes}
                        </span>
                      </button>

                      <button className="flex items-center space-x-2 group/comment hover:bg-blue-50 rounded-lg px-3 py-2 transition-all duration-200">
                        <ChatBubbleOvalLeftIcon className="w-5 h-5 text-gray-500 group-hover/comment:text-blue-500 group-hover/comment:scale-110 transition-all" />
                        <span className="text-sm font-medium text-gray-600 group-hover/comment:text-blue-500">
                          {post.comments}
                        </span>
                      </button>
                    </div>

                    {/* Time Ago */}
                    <span className="text-xs text-gray-500 font-medium">
                      {post.timeAgo}
                    </span>
                  </div>
                </div>

                {/* Interactive Bottom Border */}
                <div className="h-1 bg-gradient-to-r from-apollo-accent to-blue-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
              </div>
            ))}
          </div>

          {/* Mobile See All Button */}
          <div className="md:hidden text-center mt-8">
            <button
              onClick={() => navigate("/forums")}
              className="bg-gradient-to-r from-apollo-accent to-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center space-x-2 mx-auto group"
            >
              <span>See All Discussions</span>
              <ChevronRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Enhanced Comprehensive Footer */}
      <Footer />

      {/* Floating Chat Bot */}
      <div className="fixed bottom-6 right-6 z-50">
        {/* AI Chat Window */}
        {showAIChat && (
          <div className="mb-4 w-80 h-96 bg-white rounded-xl shadow-xl border border-gray-100 flex flex-col overflow-hidden">
            {/* Chat Header */}
            <div className="bg-apollo-primary text-white p-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <BeakerIcon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Apollo AI</h3>
                  <p className="text-xs text-blue-100">Online</p>
                </div>
              </div>
              <button
                onClick={toggleAIChat}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <ChevronDownIcon className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 p-3 overflow-y-auto space-y-2 bg-gray-50">
              {chatMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${
                    msg.type === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[75%] p-2 rounded-lg text-xs ${
                      msg.type === "user"
                        ? "bg-apollo-accent text-white"
                        : "bg-white text-gray-800 shadow-sm"
                    }`}
                  >
                    <p className="leading-relaxed">{msg.message}</p>
                    <div
                      className={`text-xs mt-1 ${
                        msg.type === "user" ? "text-blue-100" : "text-gray-400"
                      }`}
                    >
                      {msg.timestamp.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Chat Input */}
            <div className="p-3 bg-white border-t border-gray-100">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-apollo-accent text-xs"
                />
                <button
                  onClick={sendMessage}
                  disabled={!currentMessage.trim()}
                  className="px-3 py-2 bg-apollo-accent text-white rounded-lg hover:bg-apollo-primary transition-colors disabled:opacity-50 text-xs"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Chat Toggle Button - Only show when chat is closed */}
        {!showAIChat && (
          <button
            onClick={toggleAIChat}
            className="w-12 h-12 bg-apollo-accent rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center group"
          >
            <ChatBubbleLeftRightIcon className="w-6 h-6 text-white" />

            {/* Simple tooltip */}
            <div className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              Chat with AI
            </div>
          </button>
        )}
      </div>
    </div>
  );
};

export default Homepage;
